import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { Injectable } from '@angular/core';
import { Params } from '@angular/router';

import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import { append, insertItem, patch, removeItem, updateItem } from '@ngxs/store/operators';

import { DestinationAdgroupsApiService } from '@app/core/api/customers/criterion-addition/destination-adgroups-api.service';
import {
  DestinationGetParams,
  DestinationTypeApiService,
} from '@app/core/api/customers/criterion-addition/destination-type-api.service';
import { ApiEndpoint, CriterionAdditionApiRoute, CustomerApiRoute } from '@app/core/models/api.models';
import { CompositeId } from '@app/core/models/common.models';
import { ApiListEmit, ApiListEmitAction } from '@app/core/store/models/api-action.models';
import { ApiStoreConnectService } from '@app/core/store/services/api-store-connect.service';
import { CustomerState } from '@app/core/store/states/customer/customer.state';
import { assertArray } from '@app/core/utilities/array.utilities';
import { deepClone } from '@app/core/utilities/object.utilities';
import { objectCamelCaseToSnake } from '@app/core/utilities/string.utilities';
import {
  DestinationAdGroup,
  DestinationGroup,
  DestinationInstance,
  DestinationSource,
  DestinationType,
  EffectiveDestination,
} from '@app/criterion-addition/shared/models/destination.models';
import { RuleLevel, RuleType } from '@app/criterion-addition/shared/models/rule.models';
import {
  DeselectAdGroup,
  DeselectDestination,
  DeselectMatchType,
  DestinationUpdated,
  GetDestinationAdGroups,
  GetDestinationTypes,
  GetSelectedAdGroups,
  InitDestinations,
  SelectAdGroup,
  SelectDestination,
  SelectMatchType,
} from '@app/criterion-addition/shared/modules/destination-match-type/state/destination-match-type-state.actions';
import {
  DESTINATION_MATCH_TYPE_STATE,
  DestinationMatchTypeStateModel,
} from '@app/criterion-addition/shared/modules/destination-match-type/state/destination-match-type-state.models';
import {
  destinationsToGroup,
  getDefaultMatchType,
  getDestinationAdgroupId,
  isRadioCriterion,
  isValidDestination,
} from '@app/criterion-addition/shared/modules/destination-match-type/utilities/destination-match-type.utitilies';
import { CriterionAdditionState } from '@app/criterion-addition/state/criterion-addition.state';

type StateModel = DestinationMatchTypeStateModel;

type StateCtx = StateContext<StateModel>;

@State({
  name: DESTINATION_MATCH_TYPE_STATE,
})
@Injectable()
export class DestinationMatchTypeState {
  @Selector()
  static source({ source }: StateModel): DestinationSource | undefined {
    return source;
  }

  @Selector([DestinationMatchTypeState.source])
  static ruleTypeId(source: DestinationSource | undefined): number | undefined {
    return source?.ruleTypeId;
  }

  @Selector([DestinationMatchTypeState.ruleTypeId, CriterionAdditionState.ruleTypes])
  static ruleType(ruleTypeId: number | undefined, ruleTypes: RuleType[]): RuleType | undefined {
    return ruleTypes.find((ruleType) => ruleType.ruleTypeId === ruleTypeId);
  }

  @Selector([DestinationMatchTypeState.ruleType])
  static isSponsoredProductsAuto(ruleType: RuleType | undefined): boolean {
    return ruleType?.campaignType === 'AMAZON-SPONSORED-PRODUCTS-AUTO';
  }

  @Selector([DestinationMatchTypeState.source])
  static selected(source: DestinationSource | undefined): DestinationInstance[] {
    return assertArray(source?.destinations);
  }

  @Selector()
  static destinations({ destinations }: StateModel): DestinationGroup[] {
    return assertArray(destinations);
  }

  @Selector()
  static adGroups({ adGroups }: StateModel): DestinationAdGroup[] {
    return assertArray(adGroups);
  }

  @Selector()
  static selectedAdGroups({ selectedAdGroups }: StateModel): DestinationAdGroup[] {
    return assertArray(selectedAdGroups);
  }

  @Selector([DestinationMatchTypeState.ruleType])
  static isRadioCriterionType(ruleType: RuleType | undefined): boolean {
    return isRadioCriterion(ruleType);
  }

  constructor(
    private dta: DestinationTypeApiService,
    private daa: DestinationAdgroupsApiService,
    private asc: ApiStoreConnectService,
    private store: Store,
  ) {}

  @Action(InitDestinations)
  init({ dispatch, patchState }: StateCtx, { source }: InitDestinations): Observable<unknown> {
    const customerId = this.store.selectSnapshot(CustomerState.customerId);
    const ruleType = this.store.selectSnapshot(CriterionAdditionState.ruleType(source.ruleTypeId));

    this.asc.connectList<DestinationAdGroup>(GetDestinationAdGroups, {
      init: {
        searchFields: [
          'adspertAccountId',
          'accountId',
          'accountName',
          'accountTagsUser',
          'performanceGroupId',
          'performanceGroupName',
          'amzPortfolioId',
          'amzPortfolioName',
          'campaignId',
          'campaignName',
          'campaignLabels',
          'adgroupId',
          'adgroupName',
          'adgroupLabels',
        ],
        sortValues:
          ruleType?.campaignType === 'AMAZON-SPONSORED-PRODUCTS-AUTO'
            ? { amzProductAdsMatchRatio: -2, adgroupName: 3, destinationEnabled: -1 }
            : { adgroupName: 2, destinationEnabled: -1 },
        limit: 5,
        endpoint: ApiEndpoint.Customers,
        route: [customerId, CustomerApiRoute.CriterionAddition, CriterionAdditionApiRoute.DestinationAdgroups],
      },
      settings: {
        trackById: 'adgroupId',
        searchDebounceTime: 500,
      },
    });

    const newSource = deepClone(source);
    const actions = [];

    const adGroupParams = this.getParamsForAdGroups(newSource);

    patchState({ adGroups: [], selectedAdGroups: [], source: newSource, destinations: [] });

    if (adGroupParams.ruleAdspertAdgroupId || adGroupParams.proposalId) {
      actions.push(new GetDestinationAdGroups({ params: adGroupParams }));
    }

    const selectedAdGroups = assertArray(newSource.destinations)
      .map(getDestinationAdgroupId)
      .filter((adGroupId) => !!adGroupId) as CompositeId[];

    if (selectedAdGroups.length) {
      actions.push(new GetSelectedAdGroups(selectedAdGroups));
    }

    const destinationParams = this.getParamsForDestinations(newSource);

    actions.push(new GetDestinationTypes(destinationParams));

    return dispatch(actions);
  }

  @Action(GetDestinationTypes)
  getDestinationTypes({ patchState }: StateCtx, { params }: GetDestinationTypes): Observable<unknown> {
    return this.dta.getTypes(params).pipe(
      map((destinations) => {
        patchState({ destinations: destinationsToGroup(destinations) });
      }),
    );
  }

  @Action(GetSelectedAdGroups)
  getSelectedAdGroups({ setState, getState }: StateCtx, { adGroupIds }: GetSelectedAdGroups): Observable<unknown> {
    const params = this.getParamsForAdGroups(getState().source);

    return this.daa
      .getAll<DestinationAdGroup>({
        params: objectCamelCaseToSnake(params),
        filterValues: { adspertAdgroupId: adGroupIds },
      })
      .pipe(
        map((selectedAdGroups) => {
          setState(patch({ selectedAdGroups: append(selectedAdGroups) }));
        }),
      );
  }

  @Action(SelectDestination)
  selectDestination({ setState, getState, dispatch }: StateCtx, { destinationType }: SelectDestination): void {
    const destinationId = this.getNewDestinationId(getState());
    const matchType = getDefaultMatchType(destinationType);
    const isRadioCriterion = this.store.selectSnapshot(DestinationMatchTypeState.isRadioCriterionType);

    setState(
      patch({
        source: patch({
          destinations: insertItem({
            destinationId,
            destinationType,
            matchType,
            negative: isRadioCriterion ? null : false,
          } as DestinationInstance),
        }),
      }),
    );

    dispatch(new DestinationUpdated());
  }

  @Action(DeselectDestination)
  deselectDestination({ setState, getState, dispatch }: StateCtx, { destinationType }: DeselectDestination): void {
    const currentDestinations = assertArray(getState().source?.destinations);

    const targetDestinations = currentDestinations.filter(
      (destination) => destination.destinationType === destinationType,
    );

    const updatedDestinations = currentDestinations.filter(
      (destination) => destination.destinationType !== destinationType,
    );

    const adspertAdgroupIds = targetDestinations
      .map((destination) => getDestinationAdgroupId(destination))
      .filter((adGroupId) => !!adGroupId);

    const selectedAdGroups = assertArray(getState().selectedAdGroups).filter(
      (adGroup) => !adspertAdgroupIds.includes(adGroup.adspertAdgroupId),
    );

    setState(patch({ source: patch({ destinations: updatedDestinations }), selectedAdGroups }));

    dispatch(new DestinationUpdated());
  }

  @Action(SelectAdGroup)
  selectAdGroup({ setState, getState, dispatch }: StateCtx, { adGroup }: SelectAdGroup): void {
    const destinationId = this.getNewDestinationId(getState());

    setState(
      patch({
        source: patch({
          destinations: append([
            {
              destinationId,
              destinationType: DestinationType.SelectedAdgroup,
              adspertAdgroupId: adGroup.adspertAdgroupId,
              effectiveDestination: adGroup as EffectiveDestination,
            } as DestinationInstance,
          ]),
        }),
        selectedAdGroups: insertItem(adGroup),
      }),
    );

    dispatch(new DestinationUpdated());
  }

  @Action(DeselectAdGroup)
  deselectAdGroup({ setState, getState, dispatch }: StateCtx, { adGroup }: DeselectAdGroup): void {
    const destinations = assertArray(getState().source?.destinations).filter(
      (destination) => getDestinationAdgroupId(destination) !== adGroup.adspertAdgroupId,
    );

    const selectedAdGroups = assertArray(getState().selectedAdGroups).filter(
      (selected) => selected.adspertAdgroupId !== adGroup.adspertAdgroupId,
    );

    setState(patch({ source: patch({ destinations }), selectedAdGroups }));

    dispatch(new DestinationUpdated());
  }

  @Action(SelectMatchType)
  selectMatchType({ setState, getState, dispatch }: StateCtx, { destination }: SelectMatchType): void {
    const isRadioCriterion = this.store.selectSnapshot(DestinationMatchTypeState.isRadioCriterionType);
    const destinations = assertArray(getState().source.destinations);
    const adGroupId = getDestinationAdgroupId(destination);
    const realDestination = destinations.find(
      (target) =>
        target.destinationType === destination?.destinationType &&
        (!adGroupId || adGroupId === getDestinationAdgroupId(target)) &&
        !isValidDestination(target, isRadioCriterion),
    );
    const destinationId = realDestination?.destinationId || this.getNewDestinationId(getState());

    setState(
      patch({
        source: patch({
          destinations: realDestination
            ? updateItem<DestinationInstance>(
                (dest) => dest.destinationId === destinationId,
                patch({
                  ...realDestination,
                  ...destination,
                  effectiveDestination:
                    realDestination.effectiveDestination || destination.effectiveDestination
                      ? {
                          ...realDestination.effectiveDestination,
                          ...destination.effectiveDestination,
                        }
                      : null,
                  destinationId,
                }),
              )
            : insertItem<DestinationInstance>({ destinationId, ...destination }),
        }),
      }),
    );

    dispatch(new DestinationUpdated());
  }

  @Action(DeselectMatchType)
  deselectMatchType({ setState, getState, dispatch }: StateCtx, { destination }: DeselectMatchType): void {
    const destinations = assertArray(getState().source.destinations);
    const adGroupId = getDestinationAdgroupId(destination);
    const shouldUpdate =
      destinations.filter(
        (target) =>
          target.destinationType === destination?.destinationType &&
          (!adGroupId || adGroupId === getDestinationAdgroupId(target)),
      ).length === 1;
    const locator = ({ destinationId }) => destination.destinationId === destinationId;

    setState(
      patch({
        source: patch({
          destinations: shouldUpdate
            ? updateItem<DestinationInstance>(locator, patch({ matchType: null, negative: null }))
            : removeItem<DestinationInstance>(locator),
        }),
      }),
    );

    dispatch(new DestinationUpdated());
  }

  @Action(ApiListEmitAction<DestinationAdGroup>(GetDestinationAdGroups))
  getAccountGroups({ setState }: StateCtx, { response }: ApiListEmit<DestinationAdGroup>): void {
    setState(patch({ adGroups: response.data }));
  }

  private getParamsForDestinations(source: DestinationSource | undefined): DestinationGetParams | undefined {
    const { ruleTypeId, definedAt, proposalId } = source || {};

    let params: DestinationGetParams;

    if (proposalId) {
      params = { proposalId };
    } else if (ruleTypeId) {
      const ruleLevel = definedAt.level === RuleLevel.Default ? RuleLevel.Platform : definedAt.level;

      params = {
        ruleTypeId,
        ruleLevel,
        ...(ruleLevel === RuleLevel.Account ? { ruleAdspertAccountId: definedAt.adspertAccountId } : {}),
        ...(ruleLevel === RuleLevel.Campaign ? { ruleAdspertCampaignId: definedAt.adspertCampaignId } : {}),
        ...(ruleLevel === RuleLevel.AdGroup ? { ruleAdspertAdgroupId: definedAt.adspertAdgroupId } : {}),
      } as DestinationGetParams;
    }

    return params;
  }

  private getParamsForAdGroups(source: DestinationSource | undefined): Params | undefined {
    let params: Params = {};

    if (source) {
      if (source.proposalId) {
        params = { proposalId: source.proposalId };
      } else {
        params = {
          ruleTypeId: source.ruleTypeId,
          ruleAdspertAdgroupId: source.definedAt?.adspertAdgroupId,
        };
      }
    }

    return params;
  }

  private getNewDestinationId(state: StateModel): number {
    return (
      Math.min(...assertArray(state.source.destinations).map((destination) => destination.destinationId || 0), 0) - 1
    );
  }
}
